/**
 * chart-utils.ts — the pure geometry of the ported SVG charts (spec §6, docs/dataviz).
 *
 * Every chart draws in a coordinate system the size of its container (1 unit = 1 CSS px), so
 * 11px axis text is 11px on a 390px phone and inside a 12-column desktop section alike.
 * These helpers are pure and unit-tested (`web/src/test/chart-utils.test.ts`).
 */

import { fmtNum } from '../format.ts';

export interface Ticks {
  step: number;
  top: number;
  ticks: number[];
}

/**
 * Rounded y-axis ticks for a 0-based axis: at most `count` steps from a fixed ladder,
 * or a caller-supplied `step` when it already fits.
 */
export function ticksFor(max: number, count = 6, step?: number): Ticks {
  if (step && max / step <= count + 0.5) {
    const top = Math.max(step, Math.ceil(max / step) * step);
    const ticks: number[] = [];
    for (let v = 0; v <= top + 1e-9; v += step) ticks.push(v);
    return { step, top, ticks };
  }
  const ladder = [100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000, 20000, 25000, 50000, 100000, 200000, 500000, 1000000];
  for (const candidate of ladder) {
    if (max / candidate <= count) {
      const top = Math.max(candidate, Math.ceil(max / candidate) * candidate);
      const ticks: number[] = [];
      for (let v = 0; v <= top + 1e-9; v += candidate) ticks.push(v);
      return { step: candidate, top, ticks };
    }
  }
  const fallback = 1000000;
  return { step: fallback, top: Math.ceil(max / fallback) * fallback, ticks: [0, Math.ceil(max / fallback) * fallback] };
}

/** Ticks for an axis that does not start at zero (net worth). */
export function ticksBetween(min: number, max: number, step: number): { from: number; to: number; ticks: number[] } {
  const from = Math.floor(min / step) * step;
  let to = Math.ceil(max / step) * step;
  if (to === from) to += step;
  const ticks: number[] = [];
  for (let v = from; v <= to + 1e-9; v += step) ticks.push(v);
  return { from, to, ticks };
}

/**
 * A column / top stacked segment: 4px-rounded data end, square at the baseline
 * (dataviz marks spec). Returns an SVG path `d`.
 */
export function topRound(x: number, y: number, w: number, h: number, r: number): string {
  if (h <= 0) return '';
  const radius = Math.min(r, w / 2, h);
  const f = (n: number) => n.toFixed(1);
  return (
    `M${f(x)} ${f(y + h)}V${f(y + radius)}a${f(radius)} ${f(radius)} 0 0 1 ${f(radius)} ${f(-radius)}` +
    `H${f(x + w - radius)}a${f(radius)} ${f(radius)} 0 0 1 ${f(radius)} ${f(radius)}V${f(y + h)}Z`
  );
}

/** Axis tick label: plain thousands ("20,000") in a Hebrew UI — never an English "K" suffix. */
export function axisLabel(agorot: number): string {
  return agorot === 0 ? '0' : fmtNum(Math.round(agorot / 100));
}

/** Evenly spaced band positions for `n` marks inside `[left, right]`. */
export function bandScale(n: number, left: number, right: number): { band: number; center: (index: number) => number } {
  const band = n > 0 ? (right - left) / n : 0;
  return { band, center: (index: number) => left + band * index + band / 2 };
}

/** Linear scale from a value domain to a pixel range (y axes are inverted). */
export function linearScale(domainMin: number, domainMax: number, rangeMin: number, rangeMax: number): (value: number) => number {
  const span = domainMax - domainMin || 1;
  return (value: number) => rangeMin + ((value - domainMin) / span) * (rangeMax - rangeMin);
}

/** A polyline `d` through points. */
export function linePath(points: ReadonlyArray<readonly [number, number]>): string {
  return points.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
}

/** The area under a line, closed on `baselineY`. */
export function areaPath(points: ReadonlyArray<readonly [number, number]>, baselineY: number): string {
  if (!points.length) return '';
  const first = points[0]!;
  const last = points[points.length - 1]!;
  return `${linePath(points)} L${last[0].toFixed(1)} ${baselineY.toFixed(1)} L${first[0].toFixed(1)} ${baselineY.toFixed(1)} Z`;
}

/**
 * Stack markers that would collide (< `minGap` px apart) by lifting each one 30px above the
 * previous — the 30-day timeline's behaviour (spec §6.5).
 */
export function stackCollisions(xs: readonly number[], minGap = 36, lift = 30): number[] {
  const placed: Array<{ x: number; lift: number }> = [];
  return xs.map((x) => {
    let offset = 0;
    for (const p of placed) if (Math.abs(p.x - x) < minGap) offset = Math.max(offset, p.lift + lift);
    placed.push({ x, lift: offset });
    return offset;
  });
}
